/**
 * Read Slide Tool
 * 
 * Tool for reading slide content from PowerPoint presentations.
 * Uses IndexedDB snapshots and Python backend for PPTX parsing.
 * 
 * Returns LLM-friendly TEXT format to the agent, while storing JSON
 * in context resolver for downstream tools (writeSlide, etc.).
 * 
 * This separation is intentional:
 * - LLM sees: Human-readable text format (token-efficient, easy to parse)
 * - Sub-agents get: Full JSON via context resolver (shapeId, positions, etc.)
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import {
    ReadSlideParamsSchema,
    type ReadSlideParams,
    type ReadSlideResponse,
    type DetailLevel,
    type SlideRequest,
} from "./readSlide.schema";
import { createAndStoreCompositeContext } from "../../../../core/compositeIdUtils";
import { LiveTrackingStorage, SlideTrackingSnapshot } from "../../../../../services/tracking/liveTrackingStorage";
import { SlideCapture, CaptureResult } from "../../../../../services/capture/slideCapture";
import { BACKEND_BASE_URL, BackendClient } from "../../../../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { getDeckRegistry } from "../../../../../services/tracking/deckRegistry";
import { slideId as normalizeSlideId } from "../../../../../utils/idNormalizer";
import GlobalStateManager from "../../../../globals/global-state/globalStateManager";
import { getContextResolver } from "../../../../core/contextResolver";
import type { SlideInfoEntry } from "../../../../globals/llm/reminder/types";

/**
 * Result from parsing a slide via v2 backend
 */
interface ParsedSlideResult {
    /** Full JSON data from Aspose reader (camelCase) - stored in context for sub-agents */
    json: Record<string, unknown>;
    /** LLM-friendly text format - returned to orchestrator */
    text: string;
    /** Optional XML-ish format (if provided by backend) */
    xml?: string;
    /** Whether this came from v2 (Aspose) or fell back to v1 */
    isV2: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// READ SLIDE TOOL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tool for reading slides from a PowerPoint presentation
 */
export class ReadSlideTool extends ToolBase<ReadSlideParams, ReadSlideResponse> {
    public readonly name = "readSlide";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = `### readSlide
Read slides from a PowerPoint presentation. Returns slide content including shapes, text, images, and notes.

**When to use:**
- Before making ANY edits to a slide (ALWAYS read first)
- To understand current slide content and structure
- To get shape IDs for editing or copying
- To get layout information for new content placement

**Detail levels:**
- \`layout\`: Positions and sizes only (fastest, ~5-10 shapes)
- \`complete\`: Full styling details (slower, all properties)

**CRITICAL: Always read before edit. Never edit a slide without reading it first.**

\`\`\`json
{
  "slides": [
    { "slideIndex": 1, "detailLevel": "complete" },
    { "slideIndex": 5, "detailLevel": "complete", "includeImage": true }
  ]
}
\`\`\``;
    
    // Storage and capture for slide snapshots
    private storage: LiveTrackingStorage;
    private slideCapture: SlideCapture;
    private backendClient: BackendClient;
    
    // Track slides that need processing after read
    private slidesToProcess: SlideTrackingSnapshot[] = [];
    
    // Streaming operation fields
    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
        { name: "slides", type: "array" as const, list_status: true, order: 1 },
    ];
    
    listsToCheck = [
        { 
            name: "slides", 
            type: "array" as const, 
            item_type: "object" as const,
            list_status: true, 
            order: 1,
            check_sub_field: "detailLevel", // Slide request is ready when detailLevel is present
        },
    ];

    constructor() {
        super();
        this.storage = new LiveTrackingStorage();
        this.slideCapture = new SlideCapture(this.storage);
        this.backendClient = new BackendClient();
        this.description = new ToolDescription(
            "readSlide",
            "Read slides from a PowerPoint presentation. Returns slide content including shapes, text, images, and notes based on the requested detail level. Captures fresh data and triggers processing if changes detected.",
            ReadSlideParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<ReadSlideResponse> {
        // Validate and parse parameters using Zod
        const parseResult = safeParseToolParams(ReadSlideParamsSchema, parameters);
        
        if (parseResult.success === false) {
            throw new Error(parseResult.error);
        }
        
        const params: ReadSlideParams = parseResult.data;
        console.log('[ReadSlideTool] 🔧 Executing with params:', JSON.stringify(params, null, 2));

        // ARCHITECTURE FIX: Use anchored documentId from conversation context if available.
        // The parent agent injects documentId into parameters, which comes from the conversation's
        // presentationId (set at conversation spawn time). This ensures the tool operates on
        // the correct deck even if the user switches presentations mid-conversation.
        // Only fall back to getOrCreatedocumentId() for backwards compatibility.
        let documentId: string;
        const injecteddocumentId = (parameters as Record<string, unknown>)?.documentId as string | undefined;
        if (injecteddocumentId) {
            documentId = injecteddocumentId;
            console.log(`[ReadSlideTool] Using anchored deck ID from conversation: ${documentId}`);
        } else {
            try {
                documentId = await getOrCreatedocumentId();
                console.log(`[ReadSlideTool] Fallback: Got deck ID from active presentation: ${documentId}`);
                console.warn('[ReadSlideTool] ⚠️ No anchored documentId in parameters - using live lookup');
            } catch (error) {
                console.error('[ReadSlideTool] Failed to get deck ID:', error);
                throw new Error("No active deck - cannot read slides. Make sure a presentation is open.");
            }
        }

        // Initialize storage and capture
        await this.storage.initialize();
        await this.slideCapture.initialize();
        
        const imageIds: string[] = [];  // Track image IDs for _imageIds convention
        this.slidesToProcess = []; // Reset for this execution
        
        const globalState = GlobalStateManager.getInstance();

        // Collect text-format results for LLM and context IDs for sub-agents
        const slideTexts: string[] = [];
        const contextIds: string[] = [];
        const slideInfoEntries: SlideInfoEntry[] = [];
        const contextResolver = getContextResolver();

        const deckRegistry = getDeckRegistry();
        const deckInfo = deckRegistry.getDeckById(documentId);
        let deckTitle = deckInfo?.title || 'Unknown';
        let deckTotalSlides = deckInfo?.totalSlides;
        try {
            if (!deckInfo) {
                const deckState = await this.storage.getDeckState(documentId);
                if (deckState?.deckName) {
                    deckTitle = deckState.deckName;
                }
                if (!deckTotalSlides && deckState?.slideIds?.length) {
                    deckTotalSlides = deckState.slideIds.length;
                }
            }
        } catch (error) {
            console.warn('[ReadSlideTool] Failed to load deck state for overview:', error);
        }

        for (const request of params.slides) {
            try {
                console.log(`[ReadSlideTool] Reading slide ${request.slideIndex ?? request.slideId}`);
                
                // 1. Capture fresh data and sync to IndexedDB
                const captureResult = await this.captureAndGetSnapshot(documentId, request);
                if (!captureResult) {
                    console.warn(`[ReadSlideTool] Could not capture slide ${request.slideIndex ?? request.slideId}`);
                    continue;
                }
                
                const { snapshot, hasChanged, isNew } = captureResult;
                console.log(`[ReadSlideTool] Captured slide ${snapshot.slideIndex + 1}: ${isNew ? 'NEW' : hasChanged ? 'CHANGED' : 'UNCHANGED'}`);

                // Track slides that changed for async processing
                if (hasChanged || isNew) {
                    this.slidesToProcess.push(snapshot);
                }

                // 2. Parse PPTX via Python backend - get both JSON and text
                let parsedSlide: ParsedSlideResult = { 
                    json: { shapes: [] }, 
                    text: `=== SLIDE ${snapshot.slideIndex + 1} ===\n(Failed to parse)`,
                    isV2: false 
                };
                
                if (snapshot.pptxBase64) {
                    try {
                        parsedSlide = await this.parseSlideContent(
                            snapshot.pptxBase64,
                            request.detailLevel,
                            request.includeNotes ?? false
                        );
                        const shapeCount = Array.isArray(parsedSlide.json.shapes) ? (parsedSlide.json.shapes as unknown[]).length : 0;
                        console.log(`[ReadSlideTool] Parsed slide with ${shapeCount} shapes (v2: ${parsedSlide.isV2})`);
                    } catch (parseError) {
                        console.error(`[ReadSlideTool] Failed to parse slide:`, parseError);
                        // Continue with error state rather than failing entirely
                    }
                } else {
                    console.warn(`[ReadSlideTool] No pptxBase64 available for slide ${snapshot.slideIndex}`);
                }

                // 3. Handle image storage - DON'T include base64 in result!
                // Images bloat context window (can exceed 200K token limit).
                // Instead, store in contextResolver and use _imageIds convention.
                // Also store imageId in the slide JSON so sub-agents can find it via extractContextImageIds()
                let slideImageId: string | undefined;
                if (request.includeImage && snapshot.imageBase64) {
                    // snapshot.slideId is already "slide_318" format, so just add _img suffix
                    slideImageId = `${snapshot.slideId}_img_${Date.now()}`;
                    const processed = await globalState.contextResolver.storeImageAsync(
                        slideImageId,
                        snapshot.imageBase64,
                        'image/png',
                        {
                            slideId: snapshot.slideId,
                            slideIndex: snapshot.slideIndex + 1,
                            documentId: documentId,
                        }
                    );
                    imageIds.push(slideImageId);
                    console.log(`[ReadSlideTool] Stored slide image for LLM vision: ${slideImageId}${processed.wasResized ? ' (resized)' : ''}`);
                }

                // 4. Build JSON object for context storage (used by writeSlide, etc.)
                // This includes the full v2 JSON data with shapeId, positions, etc.
                // IMPORTANT: Include _imageId so sub-agents can find the screenshot via extractContextImageIds()
                // 
                // NOTE: parsedSlide.json is spread FIRST because it contains slideIndex=1 
                // (from single-slide PPTX export). We then OVERRIDE with our known-correct
                // values (slideIndex from snapshot is the actual slide number in the deck).
                
                // HACK: The Python backend generates _textFormat with "SLIDE 1" because it parses
                // a single-slide PPTX export. We fix the JSON slideIndex above, but also need to
                // fix the text format string. This is a workaround until we pass the correct
                // slideIndex to the Python backend.
                const correctSlideIndex = snapshot.slideIndex + 1;
                const correctedTextFormat = parsedSlide.text
                    .replace(/=== SLIDE \d+ ===/g, `=== SLIDE ${correctSlideIndex} ===`)
                    .replace(/(<slide\b[^>]*\bindex=")\d+(")/, `$1${correctSlideIndex}$2`);

                const deckOverview = [
                    `Deck: "${deckTitle}" (${deckTotalSlides ?? 'unknown'} slides)`,
                    `Reading slide ${correctSlideIndex} (${snapshot.slideId}) at ${request.detailLevel} level:`,
                ].join('\n');
                
                const slideJsonForContext: Record<string, unknown> = {
                    // Spread parsed JSON first (may contain slideIndex: 1 from single-slide PPTX)
                    ...parsedSlide.json,
                    // Override with our known-correct metadata (these take precedence)
                    slideId: snapshot.slideId,
                    slideIndex: correctSlideIndex,  // Convert to 1-based - THIS IS THE ACTUAL SLIDE NUMBER
                    layoutId: snapshot.layoutId,
                    layoutName: snapshot.layoutName,
                    masterTemplateId: snapshot.masterTemplateId,
                    masterTemplateName: snapshot.masterTemplateName,
                    // Add text format for agents that want it (with corrected slide index)
                    _textFormat: correctedTextFormat,
                    ...(correctedTextFormat.trim().startsWith("<slide")
                        ? { _xmlFormat: correctedTextFormat }
                        : {}),
                    readAt: new Date().toISOString(),
                    detailLevel: request.detailLevel,
                    // Include image ID so sub-agents can resolve from image cache
                    // This follows the same pattern as Brandfetch and Wikimedia tools
                    ...(slideImageId ? { _imageId: slideImageId } : {}),
                };

                // 5. Store JSON in context resolver for sub-agents
                // Use simple slideId as primary key (e.g., "slide_318")
                const contextId = snapshot.slideId;
                contextResolver.storeWithId(contextId, slideJsonForContext);
                contextIds.push(contextId);
                
                // Also store with composite ID for caching/reuse across detail levels
                createAndStoreCompositeContext(
                    documentId,
                    snapshot.slideId,
                    slideJsonForContext,
                    `read_${request.detailLevel}`
                );

                // 6. Collect text format for LLM response
                slideTexts.push(`${deckOverview}\n${correctedTextFormat}`);
                slideInfoEntries.push({
                    slideId: snapshot.slideId,
                    slideIndex: correctSlideIndex,
                    detailLevel: request.detailLevel,
                    type: 'read',
                    timestamp: Date.now(),
                });

                console.log(`[ReadSlideTool] ✅ Read slide ${snapshot.slideIndex + 1} (${contextId}) - JSON stored in context, text for LLM`);
            } catch (error) {
                console.error(`[ReadSlideTool] Error reading slide ${request.slideIndex ?? request.slideId}:`, error);
            }
        }

        // Trigger async processing for changed slides (after returning to agent)
        if (this.slidesToProcess.length > 0) {
            console.log(`[ReadSlideTool] 📤 Queueing ${this.slidesToProcess.length} changed slides for processing`);
            this.triggerProcessingAsync(documentId, this.slidesToProcess);
        }

        console.log(`[ReadSlideTool] 🎯 Returning ${slideTexts.length} slides as text, with context IDs: [${contextIds.join(', ')}]`);
        if (imageIds.length > 0) {
            console.log(`[ReadSlideTool] 🖼️ Stored ${imageIds.length} images for vision injection`);
        }
        
        // Return text format to LLM, context IDs for passing to sub-agents
        return { 
            slides: slideTexts,  // LLM sees human-readable text
            contextIds,          // Sub-agents resolve these to get JSON
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
            _slideInfos: slideInfoEntries.length > 0 ? slideInfoEntries : undefined,
            _note: "Context IDs (e.g., 'slide_318') can be passed to sub-agents via the 'context' parameter. Sub-agents will resolve these to get full slide JSON with shapeId, positions, etc."
        };
    }

    /**
     * Capture fresh slide data using SlideCapture utility
     * Returns the (possibly updated) snapshot with change info
     */
    private async captureAndGetSnapshot(
        documentId: string, 
        request: SlideRequest
    ): Promise<CaptureResult | undefined> {
        return PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load('items');
            await context.sync();

            // Find the target slide
            let targetSlide: PowerPoint.Slide | null = null;
            let slideIndex = -1;

            if (request.slideId) {
                // Find by ID
                for (let i = 0; i < slides.items.length; i++) {
                    const slide = slides.items[i];
                    slide.load('id');
                    await context.sync();
                    const cleanId = normalizeSlideId(slide.id);
                    if (cleanId === request.slideId) {
                        targetSlide = slide;
                        slideIndex = i;
                        break;
                    }
                }
            } else if (request.slideIndex !== undefined) {
                // Find by index (request uses 1-based)
                const idx = request.slideIndex - 1;
                if (idx >= 0 && idx < slides.items.length) {
                    targetSlide = slides.items[idx];
                    slideIndex = idx;
                }
            }

            if (!targetSlide) {
                console.warn(`[ReadSlideTool] Slide not found: ${request.slideIndex ?? request.slideId}`);
                return undefined;
            }

            // Use SlideCapture to capture and sync
            return this.slideCapture.captureAndSync(context, targetSlide, slideIndex, documentId);
        });
    }

    /**
     * Trigger process_slides_live asynchronously (fire and forget)
     * Called after returning data to agent
     */
    private triggerProcessingAsync(documentId: string, snapshots: SlideTrackingSnapshot[]): void {
        // Fire and forget - get deck title and send
        this.storage.getDeckState(documentId).then(deckState => {
            const deckTitle = deckState?.deckName || 'Untitled';
            
            this.backendClient.processSlides({
                documentId,
                deckTitle,
                slides: snapshots.map(snapshot => ({
                    slideId: snapshot.slideId,
                    slideIndex: snapshot.slideIndex,
                    imageBase64: snapshot.imageBase64,
                    xmlBase64: snapshot.pptxBase64,
                    metadata: {
                        layoutId: snapshot.layoutId,
                        layoutName: snapshot.layoutName,
                        masterTemplateId: snapshot.masterTemplateId,
                        masterTemplateName: snapshot.masterTemplateName,
                    }
                })),
                trigger: 'single'
            }).then(result => {
                console.log(`[ReadSlideTool] ✅ Processing triggered:`, result);
            }).catch(error => {
                console.error(`[ReadSlideTool] ❌ Failed to trigger processing:`, error);
            });
        }).catch(error => {
            console.error(`[ReadSlideTool] ❌ Failed to get deck state:`, error);
        });
    }

    /**
     * Parse slide content via Python backend (v2 Aspose-based)
     * 
     * Returns BOTH:
     * - json: Full camelCase data for context storage (shapeId, positions, etc.)
     * - text: LLM-friendly text format for orchestrator comprehension
     */
    private async parseSlideContent(
        pptxBase64: string,
        detailLevel: DetailLevel,
        includeNotes: boolean
    ): Promise<ParsedSlideResult> {
        const v2Url = `${BACKEND_BASE_URL}/slides/parse_v2`;
        console.log(`[ReadSlideTool] Calling v2 backend: ${v2Url}`);
        const response = await fetch(v2Url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pptx_base64: pptxBase64,
                detail_level: detailLevel,
                include_notes: includeNotes,
                output_format: 'all',  // Get JSON + text + XML
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`[ReadSlideTool] Parse failed: ${response.status} - ${errorText}`);
            throw new Error(`Parse failed: ${response.status}`);
        }

        const data = await response.json();
        if (!data.slide) {
            throw new Error("Parse failed: v2 response missing slide payload");
        }

        const slideXml = data.slideXml as string | undefined;
        const slideText = slideXml || data.slideText || this.generateFallbackText(data.slide);
        const outputType = slideXml ? "xml" : data.slideText ? "text" : "fallback";
        console.log(`[ReadSlideTool] ✅ v2 success: ${(data.slide.shapes || []).length} shapes, ${slideText.length} chars (${outputType})`);

        return {
            json: data.slide,
            text: slideText,
            xml: slideXml,
            isV2: true,
        };
    }

    /**
     * Generate basic text representation when v2 text format not available
     * This is a fallback if the backend omits formatted text.
     */
    private generateFallbackText(slide: Record<string, unknown>): string {
        const lines: string[] = [];
        const slideIndex = slide.slideIndex || slide.slide_index || '?';
        const layoutName = slide.layoutName || slide.layout || 'Unknown';
        
        lines.push(`=== SLIDE ${slideIndex} ===`);
        lines.push(`Layout: ${layoutName}`);
        lines.push('');
        
        const shapes = (slide.shapes || []) as Array<Record<string, unknown>>;
        lines.push(`--- SHAPES (${shapes.length} total) ---`);
        lines.push('');
        
        for (let i = 0; i < shapes.length; i++) {
            const shape = shapes[i];
            const shapeId = shape.shapeId || shape.shape_id || '?';
            const name = shape.name || 'unnamed';
            const type = ((shape.type || 'shape') as string).toUpperCase();
            const pos = shape.position as Record<string, number> | undefined;
            
            lines.push(`[${i + 1}] ${type}: ${name} (ID: ${shapeId})`);
            if (pos) {
                lines.push(`    Position: x=${pos.x}, y=${pos.y}, w=${pos.width}, h=${pos.height}`);
            }
            
            // Extract text if available
            const paragraphs = shape.paragraphs as Array<Record<string, unknown>> | undefined;
            if (paragraphs && paragraphs.length > 0) {
                for (const para of paragraphs) {
                    const text = para.text as string | undefined;
                    if (text?.trim()) {
                        const level = para.level as number || 0;
                        const indent = '  '.repeat(level);
                        lines.push(`    Text: ${indent}"${text.substring(0, 60)}${text.length > 60 ? '...' : ''}"`);
                    }
                }
            }
            
            lines.push('');
        }
        
        return lines.join('\n');
    }

}

export default ReadSlideTool;
